import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { decodeCheckbox } from "@/utils/fieldRendering/legacyBoolTokens";

// "Supported files" config values for file-upload custom fields.
export const FILE_FIELD_ANY_TYPE = "anyType";

interface FileFieldCategoryExtensions {
  [category: string]: string;
}

// Keyed in the order the admin dropdown offers the categories.
export const FILE_FIELD_CATEGORY_EXTENSIONS: FileFieldCategoryExtensions = {
  documents: ".pdf,.doc,.docx",
  spreadsheets: ".csv,.xls,.xlsx",
  presentations: ".ppt,.pptx",
  text: ".txt,.rtf,.md",
  images: ".jpg,.jpeg,.png,.webp,.gif",
  audio: ".mp3,.wav,.m4a",
  video: ".mp4,.mov,.webm"
};

export const FILE_FIELD_CATEGORY_KEYS = [
  FILE_FIELD_ANY_TYPE,
  ...Object.keys(FILE_FIELD_CATEGORY_EXTENSIONS)
];

// Empty string renders an empty `accept` attribute, which browsers treat as
// "no restriction" — required for the "Any file type" option, where the
// component's default accept list would otherwise apply.
export const getFileFieldAccept = (supportedFiles?: string): string => {
  if (!supportedFiles || supportedFiles === FILE_FIELD_ANY_TYPE) {
    return "";
  }
  return FILE_FIELD_CATEGORY_EXTENSIONS[supportedFiles] ?? "";
};

// The admin drawer writes this as a JSON boolean, but a row seeded from an
// older field can hold the checkbox token form instead. Read raw, `!"unchecked"`
// is false, so a field configured for one file silently starts accepting many.
// An absent value is the default rather than a negative answer.
export const allowsMultipleFiles = (config?: {
  allowMultipleFiles?: unknown;
}): boolean =>
  config?.allowMultipleFiles == null
    ? true
    : decodeCheckbox(config.allowMultipleFiles);

const isSavedAttachmentInfo = (entry: unknown): entry is SavedAttachmentInfo =>
  typeof entry === "object" &&
  entry !== null &&
  typeof (entry as SavedAttachmentInfo).id === "string" &&
  typeof (entry as SavedAttachmentInfo).filename === "string";

// Field values are stored as a JSON string of SavedAttachmentInfo objects.
// Entries that don't carry an id and filename are dropped rather than passed
// to the attachment controls.
export const parseFileFieldValue = (
  value: unknown
): SavedAttachmentInfo[] => {
  if (Array.isArray(value)) {
    return value.filter(isSavedAttachmentInfo);
  }
  if (typeof value !== "string" || !value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(isSavedAttachmentInfo) : [];
  } catch {
    return [];
  }
};

export const serializeFileFieldValue = (
  attachments: SavedAttachmentInfo[]
): string => JSON.stringify(attachments);
